import React, { useState } from 'react';
import { X, Trash2, Trash, Edit2, CalendarDays } from 'lucide-react';

interface GoogleCalendarModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// Helper: Google Calendar API endpoints
const CALENDAR_API_BASE = 'https://www.googleapis.com/calendar/v3';

// Helper: Get access token from localStorage
function getGoogleAccessToken() {
  return localStorage.getItem('googleAccessToken');
}

function toRFC3339(dateStr: string) {
  // If already has timezone, return as is
  if (/Z$|[+-]\d{2}:\d{2}$/.test(dateStr)) return dateStr;
  // If missing seconds, add :00
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(dateStr)) return dateStr + ':00Z';
  // If missing timezone, add Z
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(dateStr)) return dateStr + 'Z';
  return dateStr;
}

// Helper: Get recurring eventId from instanceId
function getRecurringEventId(event: any) {
  return event.recurringEventId || event.id;
}

const recurrenceOptions = [
  { label: 'None', value: '' },
  { label: 'Daily', value: 'RRULE:FREQ=DAILY' },
  { label: 'Weekly', value: 'RRULE:FREQ=WEEKLY' },
  { label: 'Monthly', value: 'RRULE:FREQ=MONTHLY' },
  { label: 'Yearly', value: 'RRULE:FREQ=YEARLY' },
];

const DEFAULT_TIMEZONE = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const GoogleCalendarModal: React.FC<GoogleCalendarModalProps> = ({ isOpen, onClose }) => {
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newEvent, setNewEvent] = useState({ summary: '', description: '', start: '', end: '', recurrence: '' });
  const [recurrenceEndType, setRecurrenceEndType] = useState<'none' | 'until' | 'count'>('none');
  const [recurrenceEndDate, setRecurrenceEndDate] = useState('');
  const [recurrenceCount, setRecurrenceCount] = useState('');
  const [editEvent, setEditEvent] = useState<any | null>(null);

  // Fetch events from Google Calendar
  const fetchEvents = async () => {
    setLoading(true);
    setError(null);
    const accessToken = getGoogleAccessToken();
    if (!accessToken) {
      setError('Google access token not found. Please login with Google.');
      setLoading(false);
      return;
    }
    try {
      const todayISO = new Date().toISOString();
      const res = await fetch(`${CALENDAR_API_BASE}/calendars/primary/events?orderBy=startTime&singleEvents=true&timeMin=${encodeURIComponent(todayISO)}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) throw new Error('Failed to fetch events');
      const data = await res.json();
      setEvents(data.items || []);
    } catch (err: any) {
      setError(err.message || 'Error fetching events');
    }
    setLoading(false);
  };

  // Create event in Google Calendar
  const handleCreateEvent = async () => {
    const accessToken = getGoogleAccessToken();
    if (!accessToken) {
      setError('Google access token not found. Please login with Google.');
      return;
    }
    // Validate recurring event duration
    if (newEvent.recurrence) {
      const startDate = new Date(newEvent.start);
      const endDate = new Date(newEvent.end);
      if (startDate.toDateString() !== endDate.toDateString()) {
        setError('For recurring events, start and end must be on the same day.');
        return;
      }
      if (endDate.getTime() <= startDate.getTime()) {
        setError('For recurring events, end time must be after start time.');
        return;
      }
    }
    setLoading(true);
    try {
      let rrule = '';
      if (newEvent.recurrence) {
        rrule = newEvent.recurrence;
        if (recurrenceEndType === 'until' && recurrenceEndDate) {
          // Format as UNTIL=YYYYMMDDT000000Z
          const until = recurrenceEndDate.replace(/-/g, '') + 'T000000Z';
          rrule += `;UNTIL=${until}`;
        } else if (recurrenceEndType === 'count' && recurrenceCount) {
          rrule += `;COUNT=${recurrenceCount}`;
        }
      }
      const eventPayload: any = {
        summary: newEvent.summary,
        description: newEvent.description,
        start: { dateTime: toRFC3339(newEvent.start), timeZone: DEFAULT_TIMEZONE },
        end: { dateTime: toRFC3339(newEvent.end), timeZone: DEFAULT_TIMEZONE },
      };
      if (rrule) {
        eventPayload.recurrence = [rrule];
      }
      const res = await fetch(`${CALENDAR_API_BASE}/calendars/primary/events`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventPayload),
      });
      if (!res.ok) throw new Error('Failed to create event');
      setShowCreate(false);
      setNewEvent({ summary: '', description: '', start: '', end: '', recurrence: '' });
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Error creating event');
    }
    setLoading(false);
  };

  // Cancel (delete) event in Google Calendar
  const handleCancelEvent = async (id: string) => {
    const accessToken = getGoogleAccessToken();
    if (!accessToken) {
      setError('Google access token not found. Please login with Google.');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${CALENDAR_API_BASE}/calendars/primary/events/${id}?sendUpdates=all`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      if (!res.ok) throw new Error('Failed to cancel event');
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Error cancelling event');
    }
    setLoading(false);
  };

  // Update event in Google Calendar
  const handleUpdateEvent = async (id: string) => {
    const accessToken = getGoogleAccessToken();
    if (!accessToken) {
      setError('Google access token not found. Please login with Google.');
      return;
    }
    setLoading(true);
    try {
      let rrule = '';
      if (newEvent.recurrence) {
        rrule = newEvent.recurrence;
        if (recurrenceEndType === 'until' && recurrenceEndDate) {
          const until = recurrenceEndDate.replace(/-/g, '') + 'T000000Z';
          rrule += `;UNTIL=${until}`;
        } else if (recurrenceEndType === 'count' && recurrenceCount) {
          rrule += `;COUNT=${recurrenceCount}`;
        }
      }
      const eventPayload: any = {
        summary: newEvent.summary,
        description: newEvent.description,
        start: { dateTime: toRFC3339(newEvent.start), timeZone: DEFAULT_TIMEZONE },
        end: { dateTime: toRFC3339(newEvent.end), timeZone: DEFAULT_TIMEZONE },
      };
      if (rrule) {
        eventPayload.recurrence = [rrule];
      }
      const res = await fetch(`${CALENDAR_API_BASE}/calendars/primary/events/${id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(eventPayload),
      });
      if (!res.ok) throw new Error('Failed to update event');
      setEditEvent(null);
      fetchEvents();
    } catch (err: any) {
      setError(err.message || 'Error updating event');
    }
    setLoading(false);
  };

  React.useEffect(() => {
    if (isOpen) fetchEvents();
  }, [isOpen]);

  // When opening edit modal, preload newEvent state
  React.useEffect(() => {
    if (editEvent) {
      setNewEvent({
        summary: editEvent.summary || '',
        description: editEvent.description || '',
        start: editEvent.start?.dateTime?.slice(0, 16) || '',
        end: editEvent.end?.dateTime?.slice(0, 16) || '',
        recurrence: editEvent.recurrence ? editEvent.recurrence[0].split(';')[0] : '',
      });
      // Optionally preload recurrence end type and value
      if (editEvent.recurrence && editEvent.recurrence[0].includes('COUNT=')) {
        setRecurrenceEndType('count');
        setRecurrenceCount(editEvent.recurrence[0].split('COUNT=')[1] || '');
      } else if (editEvent.recurrence && editEvent.recurrence[0].includes('UNTIL=')) {
        setRecurrenceEndType('until');
        setRecurrenceEndDate(editEvent.recurrence[0].split('UNTIL=')[1]?.slice(0, 8) || '');
      } else {
        setRecurrenceEndType('none');
        setRecurrenceEndDate('');
        setRecurrenceCount('');
      }
    }
  }, [editEvent]);

  function formatEventTime(start: any, end: any) {
    const startDate = start?.dateTime || start?.date;
    const endDate = end?.dateTime || end?.date;
    if (!startDate || !endDate) return 'N/A';
    const startObj = new Date(startDate);
    const endObj = new Date(endDate);
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'short', year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true
    };
    return `${startObj.toLocaleString(undefined, options)} - ${endObj.toLocaleString(undefined, options)}`;
  }

  function isValidEventTime(start, end, isRecurring) {
    if (!start || !end) return false;
    const startDate = new Date(start);
    const endDate = new Date(end);
    // For recurring events, only compare time if dates are the same
    if (isRecurring) {
      if (startDate.toDateString() !== endDate.toDateString()) return true;
      return endDate > startDate;
    }
    return endDate > startDate;
  }

  if (!isOpen) return null;

  return (
    <div className="inset-0 z-50 fixed flex items-center justify-center bg-black bg-opacity-40">
      <div className="overflow-y-auto max-h-full bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-lg p-6 relative">
        <button className="absolute top-3 right-3" onClick={onClose}>
          <X size={20} />
        </button>
        <h2 className="text-xl font-bold mb-4">Google Calendar</h2>
        {loading ? (
          <div>Loading events...</div>
        ) : error ? (
          <div className="text-red-500">{error}</div>
        ) : (
          <>
            <button className="mb-4 px-4 py-2 bg-blue-600 text-white rounded" onClick={() => setShowCreate(true)}>
              Create Event
            </button>
            {showCreate && (
              <div className="mb-4 p-4 border rounded bg-gray-50 dark:bg-gray-800">
                <input
                  type="text"
                  placeholder="Title"
                  className="mb-2 w-full p-2 border rounded"
                  value={newEvent.summary}
                  onChange={e => setNewEvent({ ...newEvent, summary: e.target.value })}
                />
                <input
                  type="text"
                  placeholder="Description"
                  className="mb-2 w-full p-2 border rounded"
                  value={newEvent.description}
                  onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
                />
                <input
                  type="datetime-local"
                  placeholder="Start"
                  className="mb-2 w-full p-2 border rounded"
                  value={newEvent.start}
                  onChange={e => setNewEvent({ ...newEvent, start: e.target.value })}
                />
                <input
                  type="datetime-local"
                  placeholder="End"
                  className="mb-2 w-full p-2 border rounded"
                  value={newEvent.end}
                  onChange={e => {
                    // For recurring events, only allow end date to match start date
                    if (newEvent.recurrence) {
                      const startDate = newEvent.start.split('T')[0];
                      const endDate = e.target.value.split('T')[0];
                      if (endDate !== startDate) {
                        // Force end date to match end date
                        const endTime = e.target.value.split('T')[1] || '';
                        setNewEvent({ ...newEvent, end: `${startDate}T${endTime}` });
                        return;
                      }
                    }
                    setNewEvent({ ...newEvent, end: e.target.value });
                  }}
                  min={newEvent.start}
                  max={newEvent.recurrence ? newEvent.start.split('T')[0] + 'T23:59' : undefined}
                />
                <select
                  className="mb-2 w-full p-2 border rounded"
                  value={newEvent.recurrence}
                  onChange={e => setNewEvent({ ...newEvent, recurrence: e.target.value })}
                >
                  {recurrenceOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                {newEvent.recurrence && (
                  <div className="mb-2">
                    <label className="block mb-1">Recurrence Ends:</label>
                    <select
                      className="mb-2 w-full p-2 border rounded"
                      value={recurrenceEndType}
                      onChange={e => setRecurrenceEndType(e.target.value as any)}
                    >
                      <option value="none">Never</option>
                      <option value="until">On Date</option>
                      <option value="count">After N Occurrences</option>
                    </select>
                    {recurrenceEndType === 'until' && (
                      <input
                        type="date"
                        className="mb-2 w-full p-2 border rounded"
                        value={recurrenceEndDate}
                        onChange={e => setRecurrenceEndDate(e.target.value)}
                      />
                    )}
                    {recurrenceEndType === 'count' && (
                      <input
                        type="number"
                        min="1"
                        className="mb-2 w-full p-2 border rounded"
                        value={recurrenceCount}
                        onChange={e => setRecurrenceCount(e.target.value)}
                        placeholder="Occurrences"
                      />
                    )}
                  </div>
                )}
                <div className="flex space-x-2">
                  <button className="px-4 py-2 bg-green-600 text-white rounded" onClick={handleCreateEvent}>Save</button>
                  <button className="px-4 py-2 bg-gray-400 text-white rounded" onClick={() => setShowCreate(false)}>Cancel</button>
                </div>
              </div>
            )}
            <ul className='h-100'>
              {events.map(event => (
                <li key={event.id} className="mb-3 p-3 border rounded-lg shadow flex justify-between items-center bg-gradient-to-r from-blue-50 to-blue-100 hover:from-blue-100 hover:to-blue-200 transition-all">
                  <div className="flex items-center gap-3">
                    <CalendarDays size={32} className="text-blue-400" />
                    <div>
                      <div className="font-semibold text-lg text-blue-900">{event.summary}</div>
                      <div className="text-xs text-gray-700 font-mono mt-1">
                        {formatEventTime(event.start, event.end)}
                      </div>
                    </div>
                  </div>
                  <div className="flex space-x-2 items-center">
                    <button title="Edit" className="p-2 rounded-full bg-blue-500 hover:bg-blue-600 text-white shadow transition" onClick={() => setEditEvent(event)}>
                      <Edit2 size={18} />
                    </button>
                    <button title="Cancel" className="p-2 rounded-full bg-red-500 hover:bg-red-600 text-white shadow transition" onClick={() => handleCancelEvent(event.id)}>
                      <Trash2 size={18} />
                    </button>
                    {event.recurringEventId && (
                      <button title="Cancel All" className="p-2 rounded-full bg-orange-500 hover:bg-orange-600 text-white shadow transition" onClick={() => handleCancelEvent(event.recurringEventId)}>
                        <Trash size={18} />
                      </button>
                    )}
                  </div>
                </li>
              ))}
              {events.length === 0 && <div>No events found.</div>}
            </ul>
          </>
        )}
      </div>
      {editEvent && (
        <div className="fixed inset-0 z-110 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-lg p-6 relative">
            <button className="absolute top-3 right-3" onClick={() => setEditEvent(null)}>
              <X size={20} />
            </button>
            <h2 className="text-xl font-bold mb-4">Edit Event</h2>
            {/* Reuse create event UI, preloaded with editEvent data */}
            <input
              type="text"
              placeholder="Title"
              className="mb-2 w-full p-2 border rounded"
              value={newEvent.summary}
              onChange={e => setNewEvent({ ...newEvent, summary: e.target.value })}
            />
            <input
              type="text"
              placeholder="Description"
              className="mb-2 w-full p-2 border rounded"
              value={newEvent.description}
              onChange={e => setNewEvent({ ...newEvent, description: e.target.value })}
            />
            <input
              type="datetime-local"
              placeholder="Start"
              className="mb-2 w-full p-2 border rounded"
              value={newEvent.start}
              onChange={e => setNewEvent({ ...newEvent, start: e.target.value })}
            />
            <input
              type="datetime-local"
              placeholder="End"
              className="mb-2 w-full p-2 border rounded"
              value={newEvent.end}
              onChange={e => setNewEvent({ ...newEvent, end: e.target.value })}
              min={newEvent.start}
              max={newEvent.recurrence ? newEvent.start.split('T')[0] + 'T23:59' : undefined}
            />
            <select
              className="mb-2 w-full p-2 border rounded"
              value={newEvent.recurrence}
              onChange={e => setNewEvent({ ...newEvent, recurrence: e.target.value })}
            >
              {recurrenceOptions.map(opt => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
            {/* Recurrence end type and value UI, reuse from create */}
            {newEvent.recurrence && (
              <div className="mb-2">
                <label className="block mb-1">Recurrence Ends:</label>
                <select
                  className="mb-2 w-full p-2 border rounded"
                  value={recurrenceEndType}
                  onChange={e => setRecurrenceEndType(e.target.value as any)}
                >
                  <option value="none">Never</option>
                  <option value="until">On Date</option>
                  <option value="count">After N Occurrences</option>
                </select>
                {recurrenceEndType === 'until' && (
                  <input
                    type="date"
                    className="mb-2 w-full p-2 border rounded"
                    value={recurrenceEndDate}
                    onChange={e => setRecurrenceEndDate(e.target.value)}
                  />
                )}
                {recurrenceEndType === 'count' && (
                  <input
                    type="number"
                    min="1"
                    className="mb-2 w-full p-2 border rounded"
                    value={recurrenceCount}
                    onChange={e => setRecurrenceCount(e.target.value)}
                    placeholder="Occurrences"
                  />
                )}
              </div>
            )}
            <div className="flex space-x-2">
              <button className="px-4 py-2 bg-green-600 text-white rounded" onClick={() => handleUpdateEvent(editEvent.id)}>
                Save
              </button>
              <button className="px-4 py-2 bg-gray-400 text-white rounded" onClick={() => setEditEvent(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarModal;
